'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useState, useMemo, Fragment } from 'react';

import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';
import { useTheme } from 'styled-components';

import useRouterNext from '@hooks/useRouterNext';
import { useSearchURL } from '@api/data-loaders/resource';
import { HotelViewModel, WebUrlType } from '@api/v3';
import { useFiltersContext } from '@contexts/Filters';
import HotelCardSkeleton from '@components/views/hotel/listing/components/ListingSkeleton/HotelCardSkeleton';
import {
  ListWrapper,
  ItemsRowWrapper,
  ListingItemContainer,
  SkeletonItemsWrapper,
  ListingCampaignBanner,
  SearchWrapperHidden,
  FAQWrapper,
  SepareteBottom,
  ListingCancelBanner,
} from '@components/views/hotel/listing/HotelListingStyle';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import IsInViewPort from '@components/ui/IsInViewPort';
import usePagination from '@components/views/hooks/usePagination';
import {
  HOTEL_SEARCH_TYPES,
  HOTEL_SORT_DATA,
  HOTEL_SORT_DATA_EXPEDİA,
} from '@constants/constants';
import { FlexibleDiv } from '@components/formStyles';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { ListingMetaViewModel } from '@components/views/types';
import { paramsToObject } from '@components/views/utils';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import { useDevice } from '@contexts/device';
import clean from '@utils/cleanObject';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';
import { getDetailChatGPTMessage } from '@components/ChatGPTLoader/utils';
import {
  useHideChatGPT,
  useSetChatGPTMessage,
} from '@components/ChatGPTLoader/hooks';
import { HotelListSchemaOrg } from '@utils/schemaorg/HotelListSchemaOrg';
import {
  useGetFavoriteListItems,
  useGetFavoriteLists,
} from '@components/views/my-favorites/hooks';
import { useAuthState } from '@contexts/auth/AuthContext';
import { useListingFAQ } from '@components/views/hooks/useListingFAQ';
import { FAQSchemaOrg } from '@utils/schemaorg/FAQSchemaOrg';
import ListingItem from '@components/ui/cards/ListingItem/next';
import { BreadCrumbHotelListSchemaOrg } from '@utils/schemaorg/BreadCrumbHotelListSchemaOrg';
import { hotelListAndClick } from '@utils/newDataLayer/productListAndClick';
import { useInsiderBannerContext } from '@contexts/insiderBanners';
import { findDefaultList } from '@components/views/my-favorites/MyFavoriteComponent/utils';
import { useHotelDifference } from '@contexts/hotel-diff/HotelDifferenceContext';
import useBasketInformation from '@hooks/useBasketInformation';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import { useListingBannerFetching } from './hooks/useListingBannerFetching';
import { useListingFetching } from './hooks/useListingFetching';
import Banner from './components/Banner/Banner';
import { getDataProps } from '../../common/propsGetters';
import { BreadcrumbWrapper } from '../detail/HotelDetailStyle';
import SortingTabs from '../components/SortingTabs';
import HotelSearchBar from './components/HotelSearchBar';
import { getHotelSearchURLType } from '../utils';
import HotelListingTitle from './components/HotelListingTitle';
import ListingFilters from './components/Filters/ListingFilters';

const ResponsiveListingBox = dynamic(
  () => import('../components/SearchBar/components/ResponsiveListingBox')
);

const HeroCampaignBanner = dynamic(
  () => import('@components/ui/banners/hero-campaign-banner/HeroCampaignBanner')
);

const ScrollToTop = dynamic(
  () => import('@components/ScrollToTop/ScrollToTop')
);

const LoyaltyController = dynamic(
  () => import('@components/ui/loyalty-verifications/LoyaltyController')
);

const AssuranceLabel = dynamic(
  () => import('@components/ui/labels/assurance/AssuranceLabel')
);

const KocAilemBanner = dynamic(
  () => import('./components/KocAilem/KocAilemBanner')
);

const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'));

const SeoText = dynamic(
  () => import('@components/views/landing/components/seoText/seoText')
);

const FAQList = dynamic(() => import('./components/FAQ'));

const Pagination = dynamic(
  () => import('@components/Pagination/next/Pagination')
);

const NotFoundHotelListing = dynamic(
  () => import('./components/NotFoundHotelListing')
);
export const updateRoomParamInUrl = (itemUrl: string): string => {
  const url = new URL(itemUrl ?? '', process.env.NEXT_PUBLIC_HOST);
  const roomParams = url.searchParams.getAll('room');
  if (roomParams.length > 0) {
    const updatedRoomParams = roomParams
      .map((param) => param.replace(/,/g, '_'))
      .join(',');
    url.searchParams.delete('room');
    url.searchParams.append('room', updatedRoomParams);
  }
  return url.toString().replace(process.env.NEXT_PUBLIC_HOST ?? '', '');
};
const HotelListing = ({
  metaInfo,
  bannerInfo,
  initialData,
  initialSearchesUrl,
  campaignBanner,
  popularSearchData,
}) => {
  const { palette } = useTheme();
  const { basketInfo } = useBasketInformation();

  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [adultCount, setAdultCount] = useState<number>(0);
  const [childCount, setChildCount] = useState<number>(0);
  const { isDesktop, isTablet, isMobile } = useDevice();
  const { setSort, sort, filters, setFilters } = useFiltersContext();
  const { push } = useRouterNext();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();

  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);
  const urlData = useSearchURL();
  const {
    location,
    activeLocation,
    hotelRoomsList,
    startDate,
    endDate,
    setDates,
    setIsFetchedItems,
    isStickySearch,
    setHotelDateSelection,
    isHotelDateSelected,
  } = useSearchBarContext();

  const actionPageLocationType = activeLocation?.type ?? urlData?.type;
  const actionPageLocationLabel = activeLocation?.text ?? urlData?.text;
  const [isSearchedFromSearchBar, setIsSearchedFromSearchBar] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const {
    diffData,
    setDiffData,
    addDiffData,
    removeDiffData,
    setVisibleInHeader,
  } = useHotelDifference();
  const { setPuanExtraBannerStatus } = useInsiderBannerContext();

  const { loginInfo } = useAuthState();
  const { favoriteLists, refetchFavoriteLists } = useGetFavoriteLists({
    token: loginInfo?.token ?? '',
  });

  const defaultList = useMemo(() => {
    return findDefaultList(favoriteLists);
  }, [favoriteLists]);

  const {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
  } = useGetFavoriteListItems({
    wishListId: defaultList?.id ?? '',
    size: 1000,
  });
  const {
    isError,
    hotels,
    isFetching,
    refetch,
    filters: hotelFilters,
    isListingSuccess,
    meta = {} as ListingMetaViewModel,
  } = useListingFetching({
    setAdultCount,
    setChildCount,
    setDates,
    setFilters,
    setIsFetchedItems,
    initialData,
  });

  const { bannerData, bannerDataIsFetching } = useListingBannerFetching({
    initialData: bannerInfo,
  });

  const { listingFAQData } = useListingFAQ(params?.slug?.toString() ?? '');

  useEffect(() => {
    if (typeof window !== 'undefined' && meta) {
      setCurrentPage(query.page ? parseInt(query.page.toString()) : 1);
    }
  }, [query]);

  // ============================
  // Handle set the chatGPT message
  // ============================
  useSetChatGPTMessage(
    getDetailChatGPTMessage(
      actionPageLocationType === WebUrlType.HOTEL_REGION ||
        actionPageLocationType === WebUrlType.HOTEL_CITY
        ? `${actionPageLocationLabel} Otelleri` ||
            `${initialSearchesUrl} Otelleri`
        : actionPageLocationLabel
    )
  );

  // ===================================================
  // Handle the sortBy click and fill the sortBy on page load
  // ===================================================
  useEffect(() => {
    if (query.sort) {
      const targetSort = HOTEL_SORT_DATA?.find(
        (item) => item.name === (query.sort as string)
      );
      if (targetSort) setSort(targetSort);
    } else {
      const defaultSort = HOTEL_SORT_DATA?.find(
        (item) => item.name === 'popularity'
      );
      setSort(defaultSort || { id: '', label: '', disabled: false, name: '' });
    }
  }, [query]);

  const handleTabClick = (activeKey: string): void => {
    setIsRedirecting(true);
    const item = HOTEL_SORT_DATA?.find((item) => item.id === activeKey);

    const currentSortItem = query?.sort ?? 'popularity';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    if (item?.name === currentSortItem) {
      if (currentSortItem === 'popularity') {
        setIsRedirecting(false);
      }
      push({
        pathname: `/${params?.slug}`,
        query: clean({ ...query, sort: 'popularity' }),
      });
      return;
    }

    if (item) {
      setSort(item); // eslint-disable-next-line @typescript-eslint/no-unused-vars
      push({
        pathname: `/${params?.slug}`,
        query: { ...query, sort: item.name },
      });
    }
  };

  const { isValidated: isKocAilemValidate } = useKocAliemContext();

  // ============================
  // Handle Pagination change
  // ============================
  usePagination(currentPage);

  // =============================================
  // Check if the current hotels are expedia hotels
  // =============================================

  const isExpediaHotels = useMemo(() => {
    const allHotels: HotelViewModel[] = [];
    if (!hotels || (hotels?.length ?? 0) === 0) return false;

    hotels?.map((hotel) => (hotel ? allHotels?.push(...hotel) : null));
    return Boolean(allHotels?.every((hotel) => hotel.expedia === true));
  }, [hotels]);

  const bannerImage = isDesktop
    ? campaignBanner?.images?.[0]?.url
    : campaignBanner?.responsiveImages?.[0]?.url;
  // ======================================
  // Render Hero Campaigns Banner
  // ======================================
  const renderHeroCampaigns = () => (
    <ListingCampaignBanner>
      <HeroCampaignBanner
        listing={true}
        data={getDataProps(!!isDesktop, palette.eclipse, campaignBanner)}
        images={bannerImage}
        pageName={meta.title + ' Listing Page'}
      />
    </ListingCampaignBanner>
  );

  useHideChatGPT(isExpediaHotels);
  // ======================================
  // Generate the breadcrumb links
  // ======================================
  const breadItems = useMemo(() => {
    const itemTitle =
      actionPageLocationType === WebUrlType.HOTEL_CHAIN ||
      actionPageLocationType === WebUrlType.HOTEL_THEME
        ? actionPageLocationLabel
        : actionPageLocationType === WebUrlType.HOTEL_REGION ||
          actionPageLocationType === WebUrlType.HOTEL_CITY
        ? `${actionPageLocationLabel} Otelleri`
        : getHotelSearchURLType(actionPageLocationType ?? '') ===
          HOTEL_SEARCH_TYPES.HOTEL_LISTING
        ? `${actionPageLocationLabel} Otelleri`
        : actionPageLocationLabel;

    return [
      {
        title: 'Ana Sayfa',
        url: '/',
      },
      !isError
        ? {
            title: itemTitle,
            url: `/${params?.slug}`,
          }
        : {},
    ];
  }, [query, isError, activeLocation]);

  // ======================================
  // Generate the banner images
  // ======================================
  const bannerImages = useMemo(() => {
    if (
      !bannerData ||
      !bannerData?.items ||
      (bannerData?.items?.length ?? 0) === 0
    )
      return [];
    return bannerData.items?.map(({ images = [], url, id }, index) => ({
      url: images?.[0]?.url,
      alt: images?.[0]?.alt ?? `${meta.title} kampanyaları ${index + 1} `,
      href: url,
      id,
    }));
  }, [bannerData, meta]);
  // ======================================
  // should shallow push if we the target page is hotel listing
  // ======================================
  const shouldShallowPush = useMemo(() => {
    return (
      location?.type === WebUrlType.HOTEL_CITY ||
      location?.type === WebUrlType.HOTEL_REGION ||
      location?.type === WebUrlType.HOTEL_CHAIN ||
      location?.type === WebUrlType.HOTEL_THEME
    );
  }, [location]);

  const toggleDateSelectionModal = () => setHotelDateSelection((val) => !val);

  const isDomestic = useMemo(() => {
    if (hotels) {
      return Boolean(hotels?.every((hotel) => hotel?.[0]?.domestic === true));
    }
    return false;
  }, [hotels]);

  SessionScopeGTM(
    'Listeleme Sayfası',
    isDomestic ? 'LP - Yurtiçi Otel' : 'LP - Yurtdışı Otel'
  );
  const hotelSliceCount = isMobile || isTablet ? 6 : 4;
  const chatGPTBannerCount = 10;

  const AssuranceLabelCount = isMobile || isTablet ? 6 : 1;

  const [isRedirecting, setIsRedirecting] = useState(false);
  const metaContentControl = metaInfo?.content === '<p><br></p>';

  useEffect(() => {
    return () => {
      setVisibleInHeader(false);
      setPuanExtraBannerStatus(false);
    };
  }, []);

  useEffect(() => {
    if (isListingSuccess && !isFetching) {
      setIsRedirecting(false);
    }
  }, [isListingSuccess, isFetching, query]);

  useEffect(() => {
    if (hotels && hotels?.length > 0 && hotels[0] && hotels[0][0]) {
      if (!hotels[0][0].expedia) {
        setVisibleInHeader(true);
        setPuanExtraBannerStatus(true);
      } else {
        setVisibleInHeader(false);
        setPuanExtraBannerStatus(false);
      }
    }
    hotels &&
      hotels?.length > 0 &&
      hotels[0]?.length > 0 &&
      hotelListAndClick(
        hotels[0],
        query.in,
        query.out,
        adultCount,
        childCount,
        sort,
        filters,
        query,
        false
      );
  }, [hotels]);

  const firstItemExpedia = useMemo(() => {
    if (hotels && hotels?.length > 0 && hotels[hotels?.length - 1]) {
      return hotels[hotels?.length - 1]?.[0]?.expedia;
    }
    return false;
  }, [hotels]);

  const updateRoomParamInUrl = (itemUrl: string): string => {
    const url = new URL(itemUrl ?? '', process.env.NEXT_PUBLIC_HOST);
    const roomParams = url.searchParams.getAll('room');
    if (roomParams.length > 0) {
      const updatedRoomParams = roomParams
        .map((param) => param.replace(/,/g, '_'))
        .join(',');
      url.searchParams.delete('room');
      url.searchParams.append('room', updatedRoomParams);
    }
    return url.toString().replace(process.env.NEXT_PUBLIC_HOST ?? '', '');
  };
  return isError || (initialData === null && popularSearchData) ? (
    <>
      <NotFoundHotelListing
        meta={meta}
        currentPage={currentPage}
        popularSearchData={popularSearchData}
      />
      {metaInfo?.content && !metaContentControl && currentPage == 1 ? (
        <SeoText text={metaInfo?.content || ''} />
      ) : null}
    </>
  ) : (
    <>
      {hotels ? (
        <HotelListSchemaOrg
          name={`${
            actionPageLocationLabel || initialSearchesUrl.text
          } Otelleri`}
          hotel={hotels}
          destination={false}
        />
      ) : null}

      {(actionPageLocationType === WebUrlType.HOTEL_CHAIN ||
        actionPageLocationType === WebUrlType.HOTEL_THEME ||
        actionPageLocationType === WebUrlType.HOTEL_REGION ||
        actionPageLocationType === WebUrlType.HOTEL_CITY) &&
      actionPageLocationLabel ? (
        <BreadCrumbHotelListSchemaOrg
          key={actionPageLocationLabel}
          actionPageLocationLabel={
            actionPageLocationType === WebUrlType.HOTEL_REGION ||
            actionPageLocationType === WebUrlType.HOTEL_CITY
              ? `${actionPageLocationLabel} Otelleri`
              : actionPageLocationLabel
          }
        />
      ) : null}
      {listingFAQData?.subjects?.length ? (
        <FAQSchemaOrg data={listingFAQData} />
      ) : null}
      {isDesktop || isTablet ? <ScrollToTop /> : null}
      <ListWrapper>
        {isDesktop ? (
          <aside>
            <ListingFilters
              setIsRedirecting={setIsRedirecting}
              isExpediaHotels={firstItemExpedia}
              hotelFilters={hotelFilters}
              isHotelsLoading={isFetching}
              hotelsCount={meta?.totalCount ?? 0}
              filterRegionText={
                activeLocation.text || initialSearchesUrl?.text || ''
              }
              isError={isError}
              isCity={actionPageLocationType === WebUrlType.HOTEL_CITY}
            />
          </aside>
        ) : null}
        <main>
          <ItemsRowWrapper>
            {isDesktop ? (
              <>
                {bannerImages?.length > 0 && !bannerDataIsFetching ? (
                  <FlexibleDiv top={20} bottom={15}>
                    <Banner images={bannerImages} logViewImpression />
                  </FlexibleDiv>
                ) : null}
                {isStickySearch ? <SearchWrapperHidden /> : null}
                {actionPageLocationType ? (
                  <HotelListingTitle
                    actionPageLocationLabel={
                      actionPageLocationLabel || initialSearchesUrl.text
                    }
                    actionPageLocationType={
                      actionPageLocationType || initialSearchesUrl?.type
                    }
                  />
                ) : null}
                <BreadcrumbWrapper>
                  <Breadcrumb
                    setIsRedirecting={setIsRedirecting}
                    items={
                      actionPageLocationType && actionPageLocationLabel
                        ? breadItems
                        : [
                            {
                              title: 'Ana Sayfa',
                              url: '/',
                            },
                            {
                              title:
                                initialSearchesUrl?.type ===
                                  WebUrlType.HOTEL_CHAIN ||
                                initialSearchesUrl?.type ===
                                  WebUrlType.HOTEL_THEME
                                  ? initialSearchesUrl?.text
                                  : initialSearchesUrl?.type ===
                                      WebUrlType.HOTEL_REGION ||
                                    initialSearchesUrl?.type ===
                                      WebUrlType.HOTEL_CITY
                                  ? `${initialSearchesUrl.text} Otelleri`
                                  : getHotelSearchURLType(
                                      initialSearchesUrl?.type ?? ''
                                    ) === HOTEL_SEARCH_TYPES.HOTEL_LISTING
                                  ? `${initialSearchesUrl?.text} Otelleri`
                                  : initialSearchesUrl?.text,
                              url: `/${params?.slug}`,
                            },
                          ]
                    }
                  />
                </BreadcrumbWrapper>

                <HotelSearchBar
                  setIsRedirecting={setIsRedirecting}
                  setIsFetchedItems={setIsFetchedItems}
                  setIsSearchedFromSearchBar={setIsSearchedFromSearchBar}
                  isSearchedFromSearchBar={isSearchedFromSearchBar}
                  shouldShallowPush={shouldShallowPush}
                />
              </>
            ) : null}

            {isMobile || isTablet ? (
              <>
                {actionPageLocationType ? (
                  <HotelListingTitle
                    actionPageLocationLabel={actionPageLocationLabel}
                    actionPageLocationType={actionPageLocationType}
                  />
                ) : null}
                <BreadcrumbWrapper>
                  <Breadcrumb
                    setIsRedirecting={setIsRedirecting}
                    items={breadItems || []}
                  />
                </BreadcrumbWrapper>

                <ResponsiveListingBox
                  setIsRedirecting={setIsRedirecting}
                  detail={true}
                  startDate={startDate}
                  endDate={endDate}
                  hotelRoomsList={hotelRoomsList}
                  isModalVisible={isShowModal || isHotelDateSelected}
                  toggleIsModalVisible={() => setIsShowModal((val) => !val)}
                  hideModal={() => {
                    setIsShowModal(false);
                    setHotelDateSelection(false);
                  }}
                  meta={meta}
                  location={location}
                  shouldShallowPush={shouldShallowPush}
                  hotelFilters={hotelFilters}
                  isFetching={isFetching}
                  hotels={hotels}
                  currentPage={currentPage}
                  isExpediaHotels={firstItemExpedia}
                  isCity={actionPageLocationType === WebUrlType.HOTEL_CITY}
                />
              </>
            ) : null}

            {!isError && isDesktop ? (
              <FlexibleDiv top={12}>
                <SortingTabs
                  select='hotel'
                  activeKey={sort.id}
                  data={
                    firstItemExpedia || isFetching
                      ? HOTEL_SORT_DATA_EXPEDİA
                      : HOTEL_SORT_DATA
                  }
                  handleTabClick={handleTabClick}
                />
              </FlexibleDiv>
            ) : null}

            {loyaltyType && !isStickySearch && !isKocAilemValidate ? (
              <KocAilemBanner hotelListing={true} />
            ) : null}

            {isFetching ||
            currentPage !==
              Number(query.page === undefined ? 1 : query.page) ? (
              <SkeletonItemsWrapper>
                {Array(9)
                  ?.fill(0)
                  ?.map((_, index) => (
                    <HotelCardSkeleton key={index} />
                  ))}
              </SkeletonItemsWrapper>
            ) : null}
            <>
              {hotels?.length > 0 && !isError ? (
                <>
                  {currentPage > 1 ? (
                    <ListingCancelBanner id='listing-cancellation-guarantee-banner' />
                  ) : null}
                  <ListingItemContainer>
                    <>
                      {hotels?.[hotels?.length - 1]?.map((item, pageIndex) => (
                        <Fragment key={pageIndex}>
                          <IsInViewPort
                            cssAll='unset'
                            key={item.id}
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                            onViewVisible={() => {}}
                          >
                            <ListingItem
                              cyprus={item.cyprus}
                              reviews={(item as HotelViewModel)?.reviews}
                              badge={item.features}
                              activeKey={sort.id}
                              boardTypes={item.boardTypes}
                              isDate={startDate ? true : false}
                              hotelListing={true}
                              seturExecutive={item.seturExecutive}
                              image={
                                item.image
                                  ? item.image
                                  : '/static/images/setur-static.jpg'
                              }
                              promotions={item.promotions}
                              adultCount={adultCount}
                              boardType={item.boardType}
                              checkInDate={query.in}
                              checkOutDate={query.out}
                              childCount={childCount}
                              domestic={item.domestic}
                              expedia={item.expedia}
                              fallBackImage='/static/images/setur-static.jpg'
                              features={[]}
                              highlights={item.highlights}
                              id={item.id}
                              location={item.location}
                              name={item.name}
                              openDatePicker={toggleDateSelectionModal}
                              pricing={item.pricing}
                              refundable={item.refundable}
                              score={item.score}
                              secure={item.secure}
                              status={item.status}
                              suggests={item.suggests}
                              themes={item.themes}
                              url={updateRoomParamInUrl(item.url ?? '')}
                              installments={item.installments}
                              distanceList={item.distanceList}
                              onGoToHotelDetailClick={() => {
                                hotelListAndClick(
                                  [item],
                                  query.in,
                                  query.out,
                                  adultCount,
                                  childCount,
                                  sort,
                                  filters,
                                  query,
                                  true
                                );
                              }}
                              hasPackage={item.hasPackage}
                              images={item.images}
                              favoriteLists={favoriteLists}
                              refetchFavoriteLists={refetchFavoriteLists}
                              defaultList={defaultList}
                              defaultListItems={
                                isFavoriteListItemError
                                  ? []
                                  : favoriteListItems?.data
                              }
                              refetchDefaultList={refetchFavoriteListItems}
                              seasonFeatures={item.seasonFeatures}
                              periodType={item.periodType}
                              initialItem={Boolean(pageIndex === 0)}
                              events={item.events}
                              differenceItem={item}
                              diffData={diffData}
                              setDiffData={setDiffData}
                              addDiffData={addDiffData}
                              removeDiffData={removeDiffData}
                              country={item.country}
                              countryCode={item.countryCode}
                              hasEvents={item.hasEvents}
                              ratings={item.ratings}
                              setIsRedirecting={setIsRedirecting}
                              basketHotelCount={basketInfo?.hotels?.length ?? 0}
                            />
                          </IsInViewPort>
                          {isDomestic ? (
                            <>
                              {pageIndex === AssuranceLabelCount - 1 &&
                              currentPage === 0 ? (
                                <AssuranceLabel
                                  hotelListing={isDesktop ? true : false}
                                  text='72 Saate Kadar Koşulsuz İptal Güvencesi'
                                />
                              ) : null}
                            </>
                          ) : null}
                          {isDesktop ? (
                            <>
                              {pageIndex === hotelSliceCount - 1 &&
                              currentPage === 1 &&
                              campaignBanner?.images?.[0]?.url &&
                              bannerImage
                                ? renderHeroCampaigns()
                                : null}
                            </>
                          ) : isMobile || isTablet ? (
                            <>
                              {pageIndex === hotelSliceCount - 1 &&
                              currentPage === 1 &&
                              campaignBanner?.responsiveImages?.[0]?.url &&
                              bannerImage
                                ? renderHeroCampaigns()
                                : null}
                            </>
                          ) : null}
                          {pageIndex === chatGPTBannerCount - 1 &&
                          currentPage === 1 &&
                          !isExpediaHotels ? (
                            <div id='chat_gpt_insider' />
                          ) : null}
                        </Fragment>
                      ))}
                      {hotelSliceCount > meta.totalCount &&
                      campaignBanner &&
                      bannerImage
                        ? renderHeroCampaigns()
                        : null}
                    </>
                  </ListingItemContainer>
                </>
              ) : null}
            </>
            {isRedirecting ? <GeneralLoader /> : null}
            {hotels?.length < meta.totalCount &&
            meta.totalPages > 1 &&
            !isError ? (
              <Pagination
                totalItemCount={meta.totalCount}
                currentPage={query.page ? +query.page : 1}
                totalPages={meta.totalPages}
                searchTitle={
                  actionPageLocationType === WebUrlType.HOTEL_CHAIN ||
                  actionPageLocationType === WebUrlType.HOTEL_THEME
                    ? actionPageLocationLabel
                    : actionPageLocationType === WebUrlType.HOTEL_REGION ||
                      actionPageLocationType === WebUrlType.HOTEL_CITY
                    ? `${actionPageLocationLabel} Otelleri`
                    : getHotelSearchURLType(actionPageLocationType ?? '') ===
                      HOTEL_SEARCH_TYPES.HOTEL_LISTING
                    ? `${actionPageLocationLabel} Otelleri`
                    : actionPageLocationLabel
                }
                type='hotel'
                onPageChange={setCurrentPage}
              />
            ) : null}
          </ItemsRowWrapper>
        </main>
      </ListWrapper>
      <SepareteBottom />

      {listingFAQData?.subjects?.length && currentPage == 1 ? (
        <FAQWrapper>
          <FAQList data={listingFAQData} />
        </FAQWrapper>
      ) : null}

      {metaInfo?.content && !metaContentControl && currentPage == 1 ? (
        <SeoText text={metaInfo?.content || ''} />
      ) : null}
      {loyaltyType && !isKocAilemValidate ? (
        <LoyaltyController loyaltyType={loyaltyType} refetch={refetch} />
      ) : null}
    </>
  );
};

export default HotelListing;
